import { useEffect, useMemo, useState } from "react";
import { collection, limit, onSnapshot, orderBy, query, type Timestamp } from "firebase/firestore";
import {
  Check,
  ChevronRight,
  CircleUserRound,
  FileCheck2,
  FileSearch,
  GitMerge,
  Inbox,
  Target,
  X,
} from "lucide-react";
import { db } from "./firebase";
import { ProvenanceButton, ProvenanceDrawer, type ProvenanceRecord } from "./ProvenanceDrawer";
import {
  decideIdentitySuggestion,
  decideMemorySuggestion,
  decideOpportunity,
  reviewAgentSuggestion,
  type MemoryReviewSuggestion,
} from "./reviewActions";

type ReviewFilter = "all" | "canonical" | "duplicates" | "opportunities" | "sources";
type MemoryRecord = { id: string; title?: string; summary?: string; status?: string; sourceType?: string; confidentiality?: string; transcriptionConfidence?: number; dataGaps?: string[]; reviewSuggestions?: MemoryReviewSuggestion[]; updatedAt?: Timestamp };
type AgentSuggestion = { id: string; type?: string; title: string; detail: string; evidence?: string; confidence?: string; urgency?: string; sourceRefs?: Array<{ collection: string; id: string; title: string }>; status: string; deferredUntil?: Timestamp; updatedAt?: Timestamp };
type IdentitySuggestion = { id: string; type?: string; acronym?: string; reason?: string; candidates?: Array<{ key?: string; name?: string }>; confidence?: string; status: string; updatedAt?: Timestamp };
type Opportunity = { id: string; title: string; thesis?: string; status?: string; stage?: string; confidence?: string; nextAction?: string; sourceMessageId?: string; supportingSourceIds?: string[]; updatedAt?: Timestamp };
type ImportJob = { id: string; fileName?: string; source?: string; status?: string; conflictCount?: number; updatedAt?: Timestamp };
type CardCandidate = { id: string; displayName?: string; company?: string; status?: string; errorDetail?: string; updatedAt?: Timestamp };

type ReviewItem = {
  id: string;
  category: Exclude<ReviewFilter, "all">;
  kind: string;
  title: string;
  detail: string;
  confidence?: string;
  urgency?: string;
  date?: Timestamp;
  provenance: ProvenanceRecord;
  approve?: () => Promise<void>;
  reject?: () => Promise<void>;
  open?: () => void;
  openLabel?: string;
};

export function ReviewInboxView({
  userId,
  isOwner,
  onOpenMemory,
  onOpenAgent,
  onOpenAdmin,
}: {
  userId: string;
  isOwner: boolean;
  onOpenMemory: (memoryOrCaptureId: string) => void;
  onOpenAgent: () => void;
  onOpenAdmin: () => void;
}) {
  const [memories, setMemories] = useState<MemoryRecord[]>([]);
  const [agentSuggestions, setAgentSuggestions] = useState<AgentSuggestion[]>([]);
  const [identitySuggestions, setIdentitySuggestions] = useState<IdentitySuggestion[]>([]);
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [importJobs, setImportJobs] = useState<ImportJob[]>([]);
  const [cards, setCards] = useState<CardCandidate[]>([]);
  const [filter, setFilter] = useState<ReviewFilter>("all");
  const [workingId, setWorkingId] = useState("");
  const [error, setError] = useState("");
  const [provenance, setProvenance] = useState<ProvenanceRecord | null>(null);

  useEffect(() => onSnapshot(query(collection(db, "users", userId, "memories"), orderBy("updatedAt", "desc"), limit(200)), (snapshot) => setMemories(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as MemoryRecord))), (reason) => setError(reason.message)), [userId]);
  useEffect(() => {
    if (!isOwner) { setAgentSuggestions([]); return; }
    return onSnapshot(query(collection(db, "users", userId, "agentSuggestions"), orderBy("updatedAt", "desc"), limit(100)), (snapshot) => setAgentSuggestions(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as AgentSuggestion))), (reason) => setError(reason.message));
  }, [isOwner, userId]);
  useEffect(() => onSnapshot(query(collection(db, "users", userId, "identitySuggestions"), orderBy("updatedAt", "desc"), limit(100)), (snapshot) => setIdentitySuggestions(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as IdentitySuggestion))), (reason) => setError(reason.message)), [userId]);
  useEffect(() => onSnapshot(query(collection(db, "users", userId, "opportunities"), orderBy("updatedAt", "desc"), limit(100)), (snapshot) => setOpportunities(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as Opportunity))), (reason) => setError(reason.message)), [userId]);
  useEffect(() => onSnapshot(query(collection(db, "users", userId, "contactImportJobs"), orderBy("updatedAt", "desc"), limit(40)), (snapshot) => setImportJobs(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as ImportJob))), (reason) => setError(reason.message)), [userId]);
  useEffect(() => onSnapshot(query(collection(db, "users", userId, "cardScanCandidates"), orderBy("updatedAt", "desc"), limit(100)), (snapshot) => setCards(snapshot.docs.map((item) => ({ id: item.id, ...item.data() } as CardCandidate))), (reason) => setError(reason.message)), [userId]);

  const items = useMemo<ReviewItem[]>(() => {
    const values: ReviewItem[] = [];
    memories.forEach((memory) => {
      if (memory.status === "review-needed") {
        values.push({
          id: `memory-quality-${memory.id}`,
          category: "sources",
          kind: "Source quality",
          title: memory.title || "Memory needs review",
          detail: memory.dataGaps?.join(" · ") || "Review the captured source before it becomes active memory.",
          confidence: memory.transcriptionConfidence === undefined ? undefined : confidenceLabel(memory.transcriptionConfidence),
          date: memory.updatedAt,
          provenance: {
            title: memory.title || "Captured memory",
            statement: memory.summary || "This captured source needs human review.",
            classification: memory.confidentiality || "personal",
            confidence: memory.transcriptionConfidence === undefined ? undefined : confidenceLabel(memory.transcriptionConfidence),
            rationale: memory.dataGaps?.join(" · "),
            sources: [{ collection: "memories", id: memory.id, title: memory.title || "Captured memory" }],
          },
          open: () => onOpenMemory(memory.id),
          openLabel: "Review capture",
        });
      }
      (memory.reviewSuggestions || []).filter((suggestion) => suggestion.status === "pending").forEach((suggestion) => {
        values.push({
          id: `memory-suggestion-${memory.id}-${suggestion.id}`,
          category: "canonical",
          kind: `Memory ${suggestion.type}`,
          title: suggestion.title,
          detail: suggestion.detail,
          date: memory.updatedAt,
          provenance: {
            title: suggestion.title,
            statement: suggestion.detail,
            classification: "pending",
            rationale: "Extracted from user-provided memory. Approval may create a canonical record.",
            sources: [{ collection: "memories", id: memory.id, title: memory.title || "Source memory", excerpt: suggestion.evidence }],
          },
          approve: () => decideMemorySuggestion(userId, memory.id, memory.reviewSuggestions || [], suggestion, "approved"),
          reject: () => decideMemorySuggestion(userId, memory.id, memory.reviewSuggestions || [], suggestion, "rejected"),
          open: () => onOpenMemory(memory.id),
          openLabel: "Open memory",
        });
      });
    });
    agentSuggestions.filter((item) => item.status === "pending" && (!item.deferredUntil?.toDate || item.deferredUntil.toDate() <= new Date())).forEach((item) => values.push({
      id: `agent-${item.id}`,
      category: "canonical",
      kind: item.type?.replaceAll("-", " ") || "Agent suggestion",
      title: item.title,
      detail: item.detail,
      confidence: item.confidence,
      urgency: item.urgency,
      date: item.updatedAt,
      provenance: {
        title: item.title,
        statement: item.detail,
        classification: "inference",
        confidence: item.confidence,
        rationale: item.evidence,
        sources: item.sourceRefs?.map((source) => ({ ...source })) || [],
      },
      approve: () => reviewAgentSuggestion(userId, item, "approve"),
      reject: () => reviewAgentSuggestion(userId, item, "reject"),
      open: onOpenAgent,
      openLabel: "Edit in Agent Learning",
    }));
    identitySuggestions.filter((item) => item.status === "pending").forEach((item) => values.push({
      id: `identity-${item.id}`,
      category: "duplicates",
      kind: "Organisation alias",
      title: item.acronym ? `Resolve ${item.acronym}` : "Identity match needs review",
      detail: item.reason || item.candidates?.map((candidate) => candidate.name).filter(Boolean).join(" · ") || "Confirm whether these records refer to the same organisation.",
      confidence: item.confidence,
      date: item.updatedAt,
      provenance: {
        title: item.acronym || "Identity suggestion",
        statement: item.reason || "Potential identity overlap.",
        classification: "pending",
        confidence: item.confidence,
        rationale: "Approving records the decision only; records are not silently merged.",
        sources: [{ collection: "identitySuggestions", id: item.id, title: "Identity suggestion" }],
      },
      approve: () => decideIdentitySuggestion(userId, item.id, "approved"),
      reject: () => decideIdentitySuggestion(userId, item.id, "rejected"),
    }));
    opportunities.filter((item) => item.status === "pending").forEach((item) => values.push({
      id: `opportunity-${item.id}`,
      category: "opportunities",
      kind: `Opportunity · ${item.stage || "Signal"}`,
      title: item.title,
      detail: item.thesis || item.nextAction || "Review this proposed opportunity.",
      confidence: item.confidence,
      date: item.updatedAt,
      provenance: {
        title: item.title,
        statement: item.thesis || "Proposed opportunity",
        classification: "inference",
        confidence: item.confidence,
        rationale: item.nextAction ? `Suggested next action: ${item.nextAction}` : "Generated opportunity awaiting human review.",
        sources: (item.supportingSourceIds || []).map((id) => ({ title: id, path: id.includes("/") ? id : undefined, excerpt: "Supporting source reference" })),
      },
      approve: () => decideOpportunity(userId, item.id, "active"),
      reject: () => decideOpportunity(userId, item.id, "dismissed"),
    }));
    importJobs.filter((item) => item.status === "needs-review" || ((item.conflictCount || 0) > 0 && item.status !== "rolled-back")).forEach((item) => values.push({
      id: `import-${item.id}`,
      category: "duplicates",
      kind: "Import conflicts",
      title: item.fileName || "Contact import",
      detail: `${item.conflictCount || 0} field conflicts require a keep-or-replace decision.`,
      date: item.updatedAt,
      provenance: {
        title: item.fileName || "Contact import",
        statement: "Imported values conflict with existing canonical contact fields.",
        classification: "imported",
        rationale: "Existing values remain unchanged until each conflict is reviewed.",
        sources: [{ collection: "contactImportJobs", id: item.id, title: `${item.source || "Contact"} import` }],
      },
      open: onOpenAdmin,
      openLabel: "Review import",
    }));
    cards.filter((item) => ["pending", "extraction-failed"].includes(item.status || "")).forEach((item) => values.push({
      id: `card-${item.id}`,
      category: "sources",
      kind: "Business card",
      title: item.displayName || "Card scan needs attention",
      detail: item.errorDetail || [item.company, "Confirm extracted fields before creating or updating a contact."].filter(Boolean).join(" · "),
      date: item.updatedAt,
      provenance: {
        title: item.displayName || "Business card capture",
        statement: "AI-extracted contact information is awaiting confirmation.",
        classification: "imported",
        rationale: item.errorDetail,
        sources: [{ collection: "cardScanCandidates", id: item.id, title: "Business card capture" }],
      },
      open: () => onOpenMemory(`card:${item.id}`),
      openLabel: "Review card",
    }));
    return values.sort((left, right) => (right.date?.toMillis?.() ?? 0) - (left.date?.toMillis?.() ?? 0));
  }, [agentSuggestions, cards, identitySuggestions, importJobs, memories, onOpenAdmin, onOpenAgent, onOpenMemory, opportunities, userId]);

  const visible = items.filter((item) => filter === "all" || item.category === filter);
  const counts = Object.fromEntries((["all", "canonical", "duplicates", "opportunities", "sources"] as ReviewFilter[]).map((value) => [value, value === "all" ? items.length : items.filter((item) => item.category === value).length]));
  const decide = async (item: ReviewItem, action: "approve" | "reject") => {
    const operation = action === "approve" ? item.approve : item.reject;
    if (!operation) return;
    try { setWorkingId(item.id); setError(""); await operation(); }
    catch (reason) { setError(reason instanceof Error ? reason.message : "Unable to save this review decision."); }
    finally { setWorkingId(""); }
  };

  return <div className="review-workspace"><header className="workspace-header"><div><h1>Unified Review Inbox</h1><p>One place for decisions that could change your canonical brain</p></div><span className="status-badge">{items.length} pending</span></header>
    {error && <div className="notice error">{error}</div>}
    <div className="review-filter-bar">{(["all", "canonical", "duplicates", "opportunities", "sources"] as ReviewFilter[]).map((value) => <button key={value} className={filter === value ? "active" : ""} onClick={() => setFilter(value)}>{reviewFilterLabel(value)}<span>{counts[value]}</span></button>)}</div>
    <div className="review-list">{visible.map((item) => <article className="review-item" key={item.id}><div className="review-kind-icon">{reviewIcon(item.category)}</div><div className="review-item-main"><header><span>{item.kind}</span><time>{formatDate(item.date)}</time></header><h2>{item.title}</h2><p>{item.detail}</p><div className="review-signals">{item.confidence && <span className={`signal ${item.confidence}`}>Confidence: {item.confidence}</span>}{item.urgency && <span className={`signal urgency-${item.urgency}`}>Urgency: {item.urgency}</span>}<ProvenanceButton onClick={() => setProvenance(item.provenance)} /></div></div><aside>{item.open && <button onClick={item.open}>{item.openLabel || "Open"}<ChevronRight size={13} /></button>}{item.reject && <button className="reject" disabled={workingId === item.id} title="Reject" onClick={() => void decide(item, "reject")}><X size={14} /></button>}{item.approve && <button className="approve" disabled={workingId === item.id} title="Approve" onClick={() => void decide(item, "approve")}><Check size={14} /></button>}</aside></article>)}
      {!visible.length && <div className="review-empty"><Inbox size={28} /><strong>Nothing waiting here</strong><span>New review decisions will appear automatically.</span></div>}
    </div>
    <ProvenanceDrawer userId={userId} record={provenance} onClose={() => setProvenance(null)} />
  </div>;
}

function reviewFilterLabel(value: ReviewFilter) {
  return ({ all: "All", canonical: "Canonical changes", duplicates: "Duplicates", opportunities: "Opportunities", sources: "Source quality" } as Record<ReviewFilter, string>)[value];
}

function reviewIcon(category: ReviewItem["category"]) {
  if (category === "canonical") return <CircleUserRound size={17} />;
  if (category === "duplicates") return <GitMerge size={17} />;
  if (category === "opportunities") return <Target size={17} />;
  if (category === "sources") return <FileCheck2 size={17} />;
  return <FileSearch size={17} />;
}

function confidenceLabel(value: number) {
  if (value >= 0.85) return "high";
  if (value >= 0.65) return "medium";
  return "low";
}

function formatDate(value?: Timestamp) {
  return value?.toDate ? new Intl.DateTimeFormat("en-SG", { dateStyle: "medium" }).format(value.toDate()) : "Recently";
}
